import { Card } from '@/components/ui/card';
import { Check } from 'lucide-react';
import { themeList, type Theme } from '@/config/themes';
import { cn } from '@/lib/utils';

interface ThemeSelectorProps {
  selectedTheme: string;
  onThemeChange: (themeId: string) => void;
  className?: string;
}

function ThemePreview({ theme, isSelected }: { theme: Theme; isSelected: boolean }) {
  return (
    <div className="flex gap-1">
      <div
        className="w-6 h-6 rounded-sm border"
        style={{ backgroundColor: theme.preview.primary }}
        title="Primary"
      />
      <div
        className="w-6 h-6 rounded-sm border"
        style={{ backgroundColor: theme.preview.secondary }}
        title="Secondary"
      />
      <div
        className="w-6 h-6 rounded-sm border"
        style={{ backgroundColor: theme.preview.accent }}
        title="Accent"
      />
      <div
        className="w-6 h-6 rounded-sm border"
        style={{ backgroundColor: theme.preview.background }}
        title="Background"
      />
    </div>
  );
}

export function ThemeSelector({ selectedTheme, onThemeChange, className }: ThemeSelectorProps) {
  return (
    <div className={cn('grid gap-3 sm:grid-cols-2', className)}>
      {themeList.map((theme) => {
        const isSelected = selectedTheme === theme.id;
        return (
          <Card
            key={theme.id}
            className={cn(
              'p-4 cursor-pointer transition-all hover:shadow-md relative',
              isSelected && 'ring-2 ring-primary border-primary'
            )}
            onClick={() => onThemeChange(theme.id)}
          >
            {isSelected && (
              <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                <Check className="h-3 w-3" />
              </div>
            )}
            <h4 className="font-medium text-sm mb-1">{theme.name}</h4>
            <p className="text-xs text-muted-foreground mb-3">{theme.description}</p>
            <ThemePreview theme={theme} isSelected={isSelected} />
          </Card>
        );
      })}
    </div>
  );
}
